import { Alert, Box, CircularProgress, Snackbar, Tooltip, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useTranslation } from "next-i18next";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { BoundDateField, BoundEuroField, BoundTextField } from "@repo/shared/components";
import { useBoundDeep } from "@repo/shared/common";
import { formatCurrencyNumber } from "@repo/legacy/components/helper/Index";
import { useInvoicePaymentValidator } from "../hooks/InvoicePaymentValidationHook";

export type PayData = {
	amount: number;
	notes: string | null;
	paymentDate: string;
	paymentMethod: string | null;
};

export type PaymentDialogShowArgs = {
	invoiceId: string;
	onPay: (invoiceId: string, data: PayData) => void;
	invoiceTotal: number;
	remainingAmount: number;
	className?: string;
};

export type PaymentDialogHandle = {
	show: (args: PaymentDialogShowArgs) => void;
	close: () => void;
};

export const PayDialog = forwardRef<PaymentDialogHandle>((_props, ref) => {
	const { t } = useTranslation();

	const [isPaying, setIsPaying] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<PaymentDialogShowArgs | null>(null);
	const { errors, setErrors, validate } = useInvoicePaymentValidator(options?.remainingAmount);

	const [paymentData, setPaymentData] = useState<PayData>({
		amount: 0,
		notes: "",
		paymentDate: "",
		paymentMethod: "",
	});

	const useBinding = <TValue,>(selector: (m: PayData) => TValue) => {
		return useBoundDeep<PayData, TValue>(paymentData, setPaymentData, selector, errors, setErrors);
	};

	const amountField = useBinding((p) => p.amount);
	const paymentMethodField = useBinding((p) => p.paymentMethod);
	const paymentDateField = useBinding((p) => p.paymentDate);
	const notesField = useBinding((p) => p.notes);

	// expose imperative API
	useImperativeHandle(
		ref,
		() => ({
			show: (args: PaymentDialogShowArgs) => {
				setOptions(args);
				setPaymentData({
					amount: 0,
					notes: "",
					paymentDate: "",
					paymentMethod: "",
				});
				setErrors({});
				setOpen(true);
			},
			close: () => {
				handleCloseInternal();
			},
		}),
		[],
	);

	const handleCloseInternal = useCallback(() => {
		setOpen(false);
		setOptions(null);
	}, []);

	const handlePayClick = async () => {
		if (!options?.onPay) return;

		const { isValid } = await validate(paymentData);

		if (!isValid) {
			return;
		}

		try {
			setIsPaying(true);

			if (errors && Object.keys(errors).length > 0) {
				setIsPaying(false);
				return;
			}

			options.onPay(options.invoiceId, paymentData);

			handleCloseInternal();
		} catch (error) {
			setErrorMessage(t("error-processing-payment"));
		} finally {
			setIsPaying(false);
		}
	};

	const handleCloseError = () => {
		setErrorMessage(null);
	};

	const handleFillRemaining = () => {
		const remaining = options?.remainingAmount || 0;
		setPaymentData({ ...paymentData, amount: remaining });
		setErrors((prev) => {
			const newErrors = { ...prev };
			delete newErrors["amount"];
			return newErrors;
		});
	};

	return (
		<Dialog
			open={open}
			maxWidth="sm"
			fullWidth
			className={options?.className}
			data-testid="payment-record-dialog">
			<DialogTitle>{t("record-payment")}</DialogTitle>
			<DialogContent className="flex-col gap-5">
				<Box sx={{ mb: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
					<Typography variant="body2" color="text.secondary">
						{t("invoice-total")}:{" "}
						<strong>{formatCurrencyNumber(options?.invoiceTotal || 0)}</strong>
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t("remaining-amount")}:{" "}
						<strong>{formatCurrencyNumber(options?.remainingAmount || 0)}</strong>
					</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
					<BoundEuroField
						label={t("amount")}
						allowNegative={true}
						boundedValue={{
							value: amountField.value,
							onChangeValue: (v: number | null) => {
								setPaymentData({ ...paymentData, amount: v || 0 });
								setErrors((prev) => {
									const newErrors = { ...prev };
									delete newErrors["amount"];
									return newErrors;
								});
							},
							errorKey: "amount",
							errorMap: errors,
						}}
						additionalProps={{ sx: { flex: 1 } }}
					/>
					<Tooltip title={t("fill-remaining-amount")}>
						<Button
							variant="outlined"
							onClick={handleFillRemaining}
							disabled={(options?.remainingAmount || 0) === 0}
							data-testid="fill-remaining-button"
							sx={{ mt: "8px", minWidth: "auto", px: 2 }}>
							{t("fill-remaining")}
						</Button>
					</Tooltip>
				</Box>
				<BoundDateField
					label={t("payment-date")}
					boundedValue={{
						value: paymentDateField.value,
						onChangeValue: (v: string | null) => {
							setPaymentData({ ...paymentData, paymentDate: v || "" });
							setErrors((prev) => {
								const newErrors = { ...prev };
								delete newErrors["paymentDate"];
								return newErrors;
							});
						},
						errorKey: "paymentDate",
						errorMap: errors,
					}}
					additionalProps={{ className: "w-100" }}
				/>
				<BoundTextField
					label={t("payment-method")}
					boundedValue={{
						value: paymentMethodField.value || "",
						onChangeValue: (value: string | null) =>
							setPaymentData({ ...paymentData, paymentMethod: value || "" }),
						errorKey: "",
					}}
					additionalProps={{ className: "w-100" }}
				/>
				<BoundTextField
					label={t("notes")}
					boundedValue={{
						value: notesField.value || "",
						onChangeValue: (value: string | null) =>
							setPaymentData({ ...paymentData, notes: value || "" }),
						errorKey: "",
					}}
					additionalProps={{ className: "w-100", multiline: true, rows: 4 }}
				/>
			</DialogContent>
			<DialogActions>
				<Button
					variant="outlined"
					onClick={handleCloseInternal}
					data-testid="payment-cancel-button">
					{t("cancel")}
				</Button>
				<Button
					variant="contained"
					onClick={handlePayClick}
					data-testid="payment-save-button"
					startIcon={isPaying ? <CircularProgress size={16} color="inherit" /> : undefined}>
					{isPaying ? t("paying") : t("pay")}
				</Button>
			</DialogActions>
			<Snackbar
				open={errorMessage !== null}
				autoHideDuration={6000}
				onClose={handleCloseError}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
				<Alert onClose={handleCloseError} severity="error" sx={{ width: "100%" }}>
					{errorMessage}
				</Alert>
			</Snackbar>
		</Dialog>
	);
});
